import clsx from "clsx";
import { GridLoader } from "react-spinners";

import { CContainer } from "@coreui/react-pro";
import { useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useTranslation } from "react-i18next";
import { HiArrowNarrowLeft } from "react-icons/hi";

import moment from "moment";


import { ROUTE_NAME_ID_2, RouteName, BASE_ROUTE } from "../../../core/constants/route_name";
import { useHomePage, useSEO } from "../../../core/hooks/db/fisioteduca/useFisioteduca";
import { useIdentityStore, useSnackbarStore } from "../../../core/store";
import { LoaderContext } from "../../components/loader/context/LoaderContext";
import { HomePage } from "../../../core/models/db";

import { UploadFile } from "../../components/drag-drop/model/UploadFile";
import { FileService } from "../../components/drag-drop/services/file.service";
import { Util } from "../../../core/util/util";
import * as MessageSnackbarDefault from "../../../core/constants/message-snackbar/msgFormDefault";
import HomePageForm from "./HomePageForm";


const HomeDetail = () => {
    const { t } = useTranslation();
    const { identity } = useIdentityStore((state) => state);
    const navigate = useNavigate();

    const [homePage, setHomePage] = useState<HomePage>(undefined);
    const [loading, setLoading] = useState(false);
    const [formHeight, setFormHeight] = useState(0);
    const formRef = useRef(null);
    const [typeAction, setTypeAction] = useState<"add" | "update">(null);

    const { handleLoading } = useContext(LoaderContext);
    const { addSnackbar, setMaxSnackbars } = useSnackbarStore();

    const { getHomePageById, addHomePage: add, updateHomePage: update } = useHomePage();
    const { checkSpecificParam: checkSpecificParamSEO } = useSEO(); // Si tienes un método para SEO

    const { getHomePages } = useHomePage();





    useEffect(() => {
        if (formRef.current) {
            setFormHeight(formRef.current.clientHeight);
        }
    }, [loading, homePage]);

    /**
     * Cargar HomePage al iniciar
     */
    useEffect(() => {
        (async () => {
            setLoading(true);

            const homePage = await getHomeAction();

            console.log("homePage", homePage);
            if (homePage?.id) {
                setHomePage(homePage);
                setTypeAction("update");
            } else {
                // Crear nuevo
                let homePage = new HomePage();
                setHomePage(homePage);
                setTypeAction("add");
            }
            setLoading(false);
        })();
    }, []);



    const getHomeAction = async () => {
        try {
            const response = await getHomePages({
                page: 1,
                itemsPerPage: 1
            });

            console.log("response", response);
            if (response?.ok && response.item.rows.length > 0) {

                return response.item.rows?.[0];
            } else {
                return null;
            }
        } catch (e) {
            return null;
        }
    }

    /**
     * Creación de archivo (subida)
     */
    const createFile = async (file: any, id: string) => {
        try {
            const { data } = await FileService.add(new UploadFile(file, ["images", "homePage", id]), null);
            if (data.ok) {
                const pathRelative = `/${data.item[0].destination}/${data.item[0].filename}`;
                return pathRelative;
            } else {
                return undefined;
            }
        } catch (e) {
            throw e;
        }
    };

    // Función auxiliar para convertir un blob a archivo y subirlo
    const convertBlob = async (
        blobStr: string,
        homePageId: string,
        prefix: string
    ): Promise<string> => {
        try {
            // Puedes ajustar la detección de extensión según lo necesites
            const extension = ".png";
            const fileName = `${prefix}_${Date.now()}${extension}`;
            const file = await Util.transformBlobToFile(blobStr, fileName);
            const { data } = await FileService.add(
                new UploadFile(file, ["images", "homePage", homePageId]),
                null
            );
            if (data.ok && data.item?.[0]) {
                return `/${data.item[0].destination}/${data.item[0].filename}`;
            }
        } catch (error) {
            console.error("Error converting blob:", error);
        }
        // Si ocurre algún error, se retorna el blob original
        return blobStr;
    };

    // Función principal para convertir todos los blobs dentro del JSON de contenido
    const convertAllBlobsFromJson = async (
        jsonString: string,
        homePageId: string
    ): Promise<string> => {
        try {
            // Parseamos el JSON
            const contentArray = JSON.parse(jsonString);

            // Iteramos sobre cada bloque de contenido
            for (const block of contentArray) {
                // Procesamos el campo "image"
                if (block.image && typeof block.image === "string" && block.image.startsWith("blob:")) {
                    // Usamos un timestamp único para este campo
                    block.image = await convertBlob(block.image, homePageId, `homePage_image_${Date.now()}`);
                }


                if (block.imageDesktop && typeof block.imageDesktop === "string" && block.imageDesktop.startsWith("blob:")) {
                    // Usamos un timestamp único para este campo
                    block.imageDesktop = await convertBlob(block.imageDesktop, homePageId, `homePage_image_desktop_${Date.now()}`);
                }

                if (block.imageTablet && typeof block.imageTablet === "string" && block.imageTablet.startsWith("blob:")) {
                    // Usamos un timestamp único para este campo
                    block.imageTablet = await convertBlob(block.imageTablet, homePageId, `homePage_image_tablet_${Date.now()}`);
                }

                if (block.imageMobile && typeof block.imageMobile === "string" && block.imageMobile.startsWith("blob:")) {
                    // Usamos un timestamp único para este campo
                    block.imageMobile = await convertBlob(block.imageMobile, homePageId, `homePage_image_phone_${Date.now()}`);
                }



                // Procesamos el campo "listImage"
                if (block.listImage && typeof block.listImage === "string" && block.listImage.startsWith("blob:")) {
                    block.listImage = await convertBlob(block.listImage, homePageId, `homePage_listImage_${Date.now()}`);
                }
                // Procesamos el array "imageTextItems"
                if (block.imageTextItems && Array.isArray(block.imageTextItems)) {
                    await Promise.all(
                        block.imageTextItems.map(async (item, i) => {
                            if (item.image && typeof item.image === "string" && item.image.startsWith("blob:")) {
                                let randomNumber = Math.floor(Math.random() * 100000 + 1);

                                // Cada imagen obtiene su propio timestamp
                                item.image = await convertBlob(item.image, homePageId, `homePage_imageText_${i}_${randomNumber}`);
                            }
                        })
                    );
                }
                // Si en el futuro se agregan otros campos que puedan contener blobs, agrégalos aquí.
            }


            return JSON.stringify(contentArray);
        } catch (error) {
            console.error("Error al convertir blobs en JSON:", error);
            return jsonString;
        }
    };

    /**
     * Update
     */
    const updateHomePage = useCallback(async (
        homePage: HomePage
    ) => {
        try {
            setLoading(true);
            const prCopy = { ...homePage };

            if (prCopy?.jsonContent) {
                console.log("entro aqui? homePage jjsonContent", prCopy.jsonContent);
                prCopy.jsonContent = await convertAllBlobsFromJson(
                    prCopy.jsonContent,
                    prCopy.id
                );
            }

            const result = await update(prCopy);
            if (result.ok) {
                setHomePage(result.item);
            }

            //  Mostrar mensaje de éxito
            addSnackbar(MessageSnackbarDefault.successEditDefault);

        } catch (error) {
            console.error("Error al actualizar HomePage", error);
            addSnackbar(MessageSnackbarDefault.errorDefault);
        } finally {
            setLoading(false);
        }
    }, [update, addSnackbar]);

    /**
     * Add
     */
    const addHomePage = useCallback(async (
        homePage: HomePage,
    ) => {
        try {
            setLoading(true);

            // 1. Crear una copia del homePagea sin la imagen principal y el jsonContent
            const homePageToCreate = { ...homePage };
            delete homePageToCreate.jsonContent;

            // 2. Crear el HomePage sin imágenes
            const createResult = await add(homePageToCreate);
            if (!createResult.ok) {
                throw new Error("Error al crear el HomePage");
            }

            const newHomePage = createResult.item;
            setTypeAction("update");
            setHomePage(newHomePage);

            const newHomePageId = newHomePage.id;


            // 4. Subir las imágenes secundarias dentro de jsonContent
            let updatedJsonContent = homePage.jsonContent;
            if (updatedJsonContent) {
                updatedJsonContent = await convertAllBlobsFromJson(updatedJsonContent, newHomePageId);
            }


            // 5. Actualizar el HomePage con las rutas de las imágenes
            const updatedHomePage = {
                ...newHomePage,
                jsonContent: updatedJsonContent
            };

            const updateResult = await update(updatedHomePage);
            if (updateResult.ok) {
                setHomePage(updateResult.item);
            } else {
                throw new Error("Error al actualizar el HomePage con las imágenes");
            }

            // 6. Mostrar mensaje de éxito
            addSnackbar(MessageSnackbarDefault.successAddDefault);

        } catch (error) {
            console.error("Error al agregar HomePage", error);
            addSnackbar(MessageSnackbarDefault.errorDefault);
        } finally {
            setLoading(false);
        }
    }, [add, update, addSnackbar]);

    /**
     * Check SEO param (si lo necesitas)
     */
    const checkHomePageSlug = async (id: string, value: string) => {
        try {
            const result = await checkSpecificParamSEO(id, value, "slug");
            return result;
        } catch (error) {
            console.error("Error al verificar el slug del HomePage", error);
            return false;
        }
    };

    /**
     * Manejo final en el formulario
     */
    const handleRecord = (
        value: HomePage
    ) => {
        if (typeAction === "add" && !value?.id) {
            addHomePage(value);
        } else if (typeAction === "update" && value?.id !== undefined) {
            updateHomePage(value);
        } else {
            console.warn("Error, no hay acción definida", typeAction, value?.id);
        }
    };

    return (
        <CContainer style={{ display: "flex", flexDirection: "column" }}>
            <div className="w-full justify-between items-center flex pt-4 pb-5">
                <div className="flex flex-col">
                    <span className="text-4xl" style={{ fontWeight: 500 }}>
                        { "Página de Home"}
                    </span>
                    <span className="small text-medium-emphasis">
                        {typeAction === "add"
                            ? "Crear HomePage"
                            : "Detalle HomePage"}
                    </span>
                </div>
            </div>

            <div className="w-full flex justify-center mb-10">
                <div className="w-full flex justify-center gap-4 flex-col xl:flex-row ">
                    <div
                        className={clsx(`flex flex-col w-full justify-center items-center`, {
                            "xl:w-[100%]": homePage?.id,
                        })}
                    >
                        <div className="bg-white block p-4 md:p-7 shadow-md md:rounded-lg w-full md:w-[70%]">
                            <div className="text-2xl font-medium mb-4">
                                {"Banners para el Home"}
                            </div>
                            <div className="w-full flex flex-col md:flex-row justify-center md:gap-4">
                                {loading && (
                                    <div
                                        className="w-full flex flex-col justify-center items-center"
                                        style={{ height: 547 }}
                                    >
                                        <GridLoader color="#3d4f65" size={25} speedMultiplier={0.7} />
                                    </div>
                                )}

                                {!loading && homePage && (
                                    <div ref={formRef} className="p-0">
                                        <HomePageForm
                                            // initialHomePage={homePage}
                                            // checkHomePageSlug={checkHomePageSlug}
                                            initialData={homePage as any}
                                            loading={loading}
                                            onSubmit={(homePage) => {
                                                handleRecord(homePage);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </CContainer>
    );
};

export default HomeDetail;
